import {
  ArrowBack,
  Download,
  Headphones,
  LibraryBooks as LibraryBooksIcon,
  OpenInNew,
  PictureAsPdf,
  PlayCircleOutline,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface ContentCategory {
  id: string;
  name: string;
  slug: string;
  is_provider_tag?: boolean;
}

interface ContentItemDetail {
  id: string;
  title: string;
  description: string;
  content_type: 'video' | 'audio' | 'pdf' | 'link';
  thumbnail_url: string;
  external_url: string;
  external_id: string;
  file_url: string;
  duration_seconds: number | null;
  duration_display: string;
  categories: ContentCategory[];
  published_at: string | null;
  is_pinned: boolean;
  is_new: boolean;
  is_featured: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Extract YouTube video ID from various URL formats.
 */
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

const ContentDetailPage: React.FC = () => {
  const { contentId } = useParams<{ contentId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [item, setItem] = useState<ContentItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const loadItem = useCallback(async () => {
    if (!contentId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        API_ENDPOINTS.CONTENT.DETAIL(contentId),
        { headers },
      );
      setItem(res.data);
    } catch {
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [contentId, token]);

  useEffect(() => {
    loadItem();
  }, [loadItem]);

  const handleTogglePin = async (): Promise<void> => {
    if (!item) return;
    try {
      const url = item.is_pinned
        ? API_ENDPOINTS.CONTENT.UNPIN(item.id)
        : API_ENDPOINTS.CONTENT.PIN(item.id);
      await axios.post(url, {}, { headers });
      setItem({ ...item, is_pinned: !item.is_pinned });
    } catch {
      setError('Failed to update pin status');
    }
  };

  const handleOpenExternal = (): void => {
    if (!item) return;
    const url = item.external_url || item.file_url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderPreview = (): React.ReactElement | null => {
    if (!item) return null;

    switch (item.content_type) {
      case 'video': {
        const videoId = extractYouTubeId(item.external_url) || item.external_id;
        if (videoId) {
          return (
            <Box sx={{ width: '100%', aspectRatio: '16/9', bgcolor: '#000', borderRadius: '12px', overflow: 'hidden' }}>
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0&showinfo=0&disablekb=1&iv_load_policy=3`}
                title={item.title}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </Box>
          );
        }
        // Fallback: thumbnail with play button
        return (
          <Box
            onClick={handleOpenExternal}
            sx={{
              width: '100%',
              aspectRatio: '16/9',
              bgcolor: '#000',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {item.thumbnail_url && (
              <img
                src={item.thumbnail_url}
                alt={item.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
              />
            )}
            <PlayCircleOutline sx={{ color: '#FFFFFF', fontSize: 64, zIndex: 1, opacity: 0.9 }} />
          </Box>
        );
      }

      case 'audio':
        return (
          <Box
            sx={{
              width: '100%',
              py: 4,
              bgcolor: '#F5F5F5',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {item.thumbnail_url ? (
              <Box sx={{ width: 120, height: 120, borderRadius: '12px', overflow: 'hidden' }}>
                <img
                  src={item.thumbnail_url}
                  alt={item.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ) : (
              <Headphones sx={{ fontSize: 64, color: '#1DB954' }} />
            )}
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              onClick={handleOpenExternal}
              sx={{
                bgcolor: '#1DB954',
                '&:hover': { bgcolor: '#1AA34A' },
                borderRadius: '20px',
                textTransform: 'none',
              }}
            >
              Listen on external app
            </Button>
          </Box>
        );

      case 'pdf':
        if (item.thumbnail_url) {
          return (
            <Box
              onClick={item.file_url ? () => window.open(item.file_url, '_blank', 'noopener,noreferrer') : undefined}
              sx={{
                width: '100%',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #E0E0E0',
                cursor: item.file_url ? 'pointer' : 'default',
                position: 'relative',
              }}
            >
              <img
                src={item.thumbnail_url}
                alt={item.title}
                style={{ width: '100%', display: 'block' }}
              />
              {item.file_url && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <OpenInNew sx={{ color: '#FFF', fontSize: 16 }} />
                  <Typography sx={{ color: '#FFF', fontSize: 12, fontWeight: 600 }}>
                    Open PDF
                  </Typography>
                </Box>
              )}
            </Box>
          );
        }
        if (item.file_url) {
          return (
            <Box sx={{ width: '100%', height: 500, borderRadius: '12px', overflow: 'hidden', border: '1px solid #E0E0E0' }}>
              <iframe
                src={item.file_url}
                title={item.title}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          );
        }
        return (
          <Box
            sx={{
              width: '100%',
              py: 4,
              bgcolor: '#F5F5F5',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <PictureAsPdf sx={{ fontSize: 64, color: '#E53935' }} />
            <Typography color="text.secondary">PDF preview not available</Typography>
          </Box>
        );

      default:
        return (
          <Box
            sx={{
              width: '100%',
              py: 4,
              bgcolor: '#F5F5F5',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LibraryBooksIcon sx={{ fontSize: 64, color: '#666' }} />
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        height: 'var(--app-height, 100vh)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
      }}
    >
      <AppHeader
        title="Content"
        leftContent={
          <IconButton
            aria-label="Back"
            onClick={() => navigate('/content')}
            sx={{
              width: 48,
              height: 44,
              bgcolor: '#AFAFAF',
              borderRadius: '32px',
              '&:hover': { bgcolor: '#9E9E9E' },
            }}
          >
            <ArrowBack sx={{ color: '#FFFFFF' }} />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : item ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Content preview */}
            {renderPreview()}

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 700,
                color: '#333',
              }}
            >
              {item.title}
              {item.is_new && (
                <Chip
                  label="NEW!"
                  size="small"
                  sx={{
                    ml: 1,
                    height: 20,
                    bgcolor: '#FFD400',
                    color: '#333',
                    fontWeight: 700,
                    '& .MuiChip-label': { px: 0.75, fontSize: 11 },
                  }}
                />
              )}
            </Typography>

            {/* Category chips — all chips on provider content are blue */}
            {item.categories.length > 0 && (() => {
              const isProviderContent = item.categories.some((c) => c.is_provider_tag);
              return (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {item.categories.map((cat) => (
                    <Chip
                      key={cat.id}
                      label={cat.name}
                      size="small"
                      sx={{
                        bgcolor: isProviderContent ? '#2F80B7' : '#FFD400',
                        color: isProviderContent ? '#FFFFFF' : '#333',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  ))}
                </Box>
              );
            })()}

            {/* Duration */}
            {item.duration_display && (
              <Typography variant="body2" color="text.secondary">
                Duration: {item.duration_display}
              </Typography>
            )}

            {/* Description */}
            {item.description && (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  color: '#555',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {item.description}
              </Typography>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              <Button
                variant="contained"
                startIcon={item.is_pinned ? <Star /> : <StarBorder />}
                onClick={handleTogglePin}
                sx={{
                  bgcolor: item.is_pinned ? '#FFD400' : '#E0E0E0',
                  color: '#333',
                  '&:hover': { bgcolor: item.is_pinned ? '#FFC800' : '#D0D0D0' },
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {item.is_pinned ? 'Pinned' : 'Pin'}
              </Button>

              {item.content_type === 'pdf' && item.file_url && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => {
                    // Reason: fetch as blob to force browser download
                    // instead of opening in a new tab
                    fetch(item.file_url)
                      .then((res) => res.blob())
                      .then((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = item.title.replace(/[^a-zA-Z0-9_-]/g, '_') + '.pdf';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      });
                  }}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#AFAFAF',
                    color: '#555',
                  }}
                >
                  Download
                </Button>
              )}

              {(item.external_url || item.file_url) && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  onClick={handleOpenExternal}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#AFAFAF',
                    color: '#555',
                  }}
                >
                  Open
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              Content not found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContentDetailPage;
